import { UUIDs } from "@patient-watch/types";
import { colors } from "./colors";

export interface ChartBackgroundBand {
    y1: number;
    y2: number;
    fill: string;
    /** Solid colour for legend swatches (the band fill is deliberately pale). */
    swatch?: string;
    label: string;
}

/** Clinical window that shows every WHO-style BMI category on the Y-axis. */
export const BMI_CHART_Y_MIN = 15;
export const BMI_CHART_Y_MAX = 40;

/** Input definition bounds from the BMI form (kg/m²). */
export const BMI_INPUT_Y_MIN = 10;
export const BMI_INPUT_Y_MAX = 80;

/** Category edges matching the standard BMI gauge (underweight through extremely obese). */
export const BMI_CATEGORY_EDGES = [18.5, 25, 30, 35] as const;

export const BMI_CATEGORY_BANDS: ChartBackgroundBand[] = [
    {
        y1: Number.NEGATIVE_INFINITY,
        y2: 18.5,
        fill: colors["blue-100"],
        swatch: colors["blue-400"],
        label: "Underweight",
    },
    { y1: 18.5, y2: 25, fill: colors["green-100"], swatch: colors["green-400"], label: "Normal" },
    { y1: 25, y2: 30, fill: colors["yellow-100"], swatch: colors["yellow-400"], label: "Overweight" },
    { y1: 30, y2: 35, fill: colors["orange-100"], swatch: colors["orange-400"], label: "Obese" },
    {
        y1: 35,
        y2: Number.POSITIVE_INFINITY,
        fill: colors["red-100"],
        swatch: colors["red-400"],
        label: "Extremely obese",
    },
];

export function isBmiForm(formId: string): boolean {
    return (
        formId === UUIDs.forms.bmi ||
        formId === UUIDs.forms.dmHeightWeight ||
        formId === "form-bmi-demo" ||
        formId === "body-mass-index" ||
        formId === "dm-height-weight" ||
        /(^|[-_])bmi($|[-_])/i.test(formId)
    );
}

/** Keep the full category window visible; expand only when a plotted value sits outside it. */
export function resolveBmiYDomain(dataMin: number | null, dataMax: number | null): [number, number] {
    let min = BMI_CHART_Y_MIN;
    let max = BMI_CHART_Y_MAX;

    if (dataMin != null && Number.isFinite(dataMin) && dataMin < min) {
        min = Math.max(BMI_INPUT_Y_MIN, Math.floor(dataMin));
    }
    if (dataMax != null && Number.isFinite(dataMax) && dataMax > max) {
        max = Math.min(BMI_INPUT_Y_MAX, Math.ceil(dataMax));
    }

    if (min >= max) {
        return [BMI_CHART_Y_MIN, BMI_CHART_Y_MAX];
    }

    return [min, max];
}

export function buildBmiYAxisTicks(yMin: number, yMax: number): number[] {
    const marks = [yMin, ...BMI_CATEGORY_EDGES, yMax];
    const unique: number[] = [];

    for (const mark of marks) {
        if (mark < yMin || mark > yMax) continue;
        if (unique.some((existing) => Math.abs(existing - mark) < 0.05)) continue;
        unique.push(mark);
    }

    return unique.sort((a, b) => a - b);
}

export function clipBackgroundBandsToDomain(
    bands: ChartBackgroundBand[],
    yMin: number,
    yMax: number
): ChartBackgroundBand[] {
    return bands
        .map((band) => ({
            ...band,
            y1: Math.max(band.y1, yMin),
            y2: Math.min(band.y2, yMax),
        }))
        .filter((band) => band.y2 > band.y1);
}
